import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { authApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Icon,
  Input,
  PhoneInput,
  useToast,
} from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { accountDetailsSchema, changePasswordSchema, closeAccountSchema } from '@/validations';
import { SignInName } from '@/features/auth/SignInName';

/**
 * The student's login details and security, shown in the "Account" section
 * of the profile page. Each card is its own form: they save to different
 * endpoints, and a mistake in one must not block the others.
 */
export function AccountSettings() {
  return (
    <div className="space-y-6">
      <DetailsCard />
      <PasswordCard />
      <CloseAccountCard />
    </div>
  );
}

function DetailsCard() {
  const { user, refreshUser } = useAuth();
  const toast = useToast();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: zodResolver(accountDetailsSchema),
    // A phone saved before the libphonenumber rule is checked once the
    // student edits it or saves, not on sight: it is their number, and the
    // form should not open with a red field they did not touch.
    mode: 'onChange',
    defaultValues: { full_name: user?.full_name ?? '', phone: user?.phone ?? '' },
  });

  // A name saved before these checks existed should say what is wrong on sight.
  useEffect(() => {
    void trigger('full_name');
  }, [trigger]);

  async function onSubmit(values) {
    try {
      // An empty phone clears it rather than storing "".
      await authApi.updateAccount({ full_name: values.full_name, phone: values.phone || null });
      await refreshUser();
      reset(values);
      toast.success('Account updated');
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        toast.error(errorMessage(error, 'Could not update the account'));
      }
    }
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <CardHeader
          title="Details"
          description="How you sign in and how companies can reach you."
          action={
            <div className="flex items-center gap-2">
              <span className="text-ink-muted text-sm">Email status:</span>
              {user?.is_email_verified ? (
                <Badge tone="success" dot>
                  Verified
                </Badge>
              ) : (
                <Badge tone="warning" dot>
                  Not verified
                </Badge>
              )}
            </div>
          }
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Email"
            value={user?.email ?? ''}
            disabled
            hint="Your sign-in address. It cannot be changed here."
            wrapperClassName="sm:col-span-2"
          />
          <Input
            label="Full name"
            required
            autoComplete="name"
            error={errors.full_name?.message}
            {...register('full_name')}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInput label="Phone" error={errors.phone?.message} {...field} />
            )}
          />
        </div>
        <div className="mt-6 flex justify-end">
          <Button type="submit" isLoading={isSubmitting} disabled={!isDirty}>
            Save details
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { new_password: '', confirm_password: '' },
  });

  async function onSubmit(values) {
    try {
      await authApi.changePassword({ new_password: values.new_password });
      reset();
      toast.success('Password changed. Other sessions were signed out.');
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        toast.error(errorMessage(error, 'Could not change the password'));
      }
    }
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <CardHeader title="Password" description="Changing it signs out every other session." />
        <SignInName />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            hint="At least 8 characters, with upper and lower case and a number."
            error={errors.new_password?.message}
            {...register('new_password')}
          />
          <Input
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            required
            error={errors.confirm_password?.message}
            {...register('confirm_password')}
          />
        </div>
        <div className="mt-6 flex justify-end">
          <Button type="submit" isLoading={isSubmitting}>
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}

function CloseAccountCard() {
  const { signOut } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({ resolver: zodResolver(closeAccountSchema), defaultValues: { password: '' } });

  const closeAccount = useMutation({
    mutationFn: () => authApi.deleteAccount({ password: getValues('password') }),
    onSuccess: async () => {
      toast.success('Your account has been closed.');
      await signOut();
      navigate(paths.home, { replace: true });
    },
    onError: (error) => {
      setConfirming(false);
      toast.error(errorMessage(error, 'Could not close the account'));
    },
  });

  return (
    <Card className="border-danger-200">
      <form onSubmit={handleSubmit(() => setConfirming(true))} noValidate>
        <CardHeader
          title="Close your account"
          description="Closing your account removes your profile from search. Applications you have already sent will stay with the companies you applied to."
        />
        <Alert tone="warning" className="mb-4">
          This is permanent. Once your account is closed, it cannot be restored.
        </Alert>
        <SignInName />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <Input
            label="Confirm your password"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            wrapperClassName="w-full sm:max-w-sm"
            {...register('password')}
          />
          {/* Level with the input, not its error line: clears the label row. */}
          <Button type="submit" variant="danger" className="sm:mt-[1.625rem]">
            <Icon name="trash" size={16} />
            Close account
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        title="Close your account?"
        message="You will be signed out immediately and your profile will no longer be visible to companies."
        confirmLabel="Close account"
        isLoading={closeAccount.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => closeAccount.mutate()}
      />
    </Card>
  );
}
